import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateClient } from "@/lib/api";
import { logger } from "@/lib/logger";

interface ClientCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the newly created client's id once creation succeeds. */
  onCreated: (clientId: number) => void;
}

/**
 * Minimal "name only" creation dialog for a brand-new client. Brand fields
 * (colors, logo, theme) are filled in afterwards via `ClientEditDialog` --
 * `onCreated` lets the caller chain straight into editing the fresh client.
 */
export function ClientCreateDialog({ open, onOpenChange, onCreated }: ClientCreateDialogProps) {
  const createClient = useCreateClient();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const creating = createClient.isPending;

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) {
      setName("");
      setError("");
    }
  }

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    setError("");
    try {
      const created = await createClient.mutateAsync({ name: trimmed });
      if (created) {
        handleOpenChange(false);
        onCreated(created.id);
      }
    } catch (err) {
      logger.error("[client-create] failed:", err);
      setError("Failed to create client. Please try again.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>New client</DialogTitle>
          <DialogDescription>
            Give the client a name. You can set its brand -- logo, colors, and theme -- next.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-1.5">
            <Label htmlFor="client-create-name">Name</Label>
            <Input
              id="client-create-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Corp"
              maxLength={100}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreate();
                }
              }}
            />
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!name.trim() || creating}>
              {creating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
